import { CircleAlert, Info } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { CurrencyValue, DateValue } from '@/components/common'
import type { TrainingNominationDecisionContext } from '@/features/training'

/**
 * The place being asked for, what it costs, and what the budget says about it.
 *
 * <p>"No budget covers this" is rendered as a warning rather than as a blank, because it is
 * the answer an approver is most likely to misread as "within budget" — and the only one
 * where the money has nobody watching it.
 */
export function TrainingNominationContext(
  { detail }: { detail: TrainingNominationDecisionContext },
) {
  const { nomination, session, course, budget } = detail
  const overBudget = (nomination.budgetOverage ?? 0) > 0

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-[var(--color-heading)]">
          {nomination.employeeName ?? nomination.employeeCode} on {course.title}
        </h3>
        <p className="mt-2 text-sm text-[var(--color-muted)]">
          Session {session.reference} · <DateValue value={session.startsOn} /> to{' '}
          <DateValue value={session.endsOn} />
          {session.location && <> · {session.location}</>}
        </p>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          {detail.costOfThisPlace == null
            ? 'This session prices nothing, so the place has no recorded cost.'
            : (
              <>
                This place costs{' '}
                <CurrencyValue
                  value={detail.costOfThisPlace}
                  currency={session.currency ?? undefined}
                />
                .
              </>
            )}
        </p>
      </Card>

      <Card
        className={`flex items-start gap-2 p-4 ${
          nomination.budgetChecked
            ? overBudget ? 'border border-[var(--color-warning)]' : ''
            : 'border border-[var(--color-warning)] bg-[var(--color-tint-amber)]'
        }`}
      >
        {nomination.budgetChecked
          ? <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          : <CircleAlert className="mt-0.5 size-4 shrink-0 text-[var(--color-warning)]"
              aria-hidden="true" />}
        <div className="text-sm">
          <p className="font-medium text-[var(--color-heading)]">
            {nomination.budgetChecked
              ? overBudget ? 'This takes the year over budget' : 'Within budget'
              : 'No budget was checked'}
          </p>
          <p className="mt-1 text-[var(--color-muted)]">
            {detail.budgetNote ?? 'The budget check recorded nothing.'}
          </p>
          {budget && (
            <p className="mt-1 text-[var(--color-muted)]">
              {budget.fiscalYear}:{' '}
              <CurrencyValue value={budget.committed} currency={budget.currency} /> committed of{' '}
              <CurrencyValue value={budget.budgetAmount} currency={budget.currency} />.
            </p>
          )}
        </div>
      </Card>

      <Card className="p-4">
        <h3 className="text-sm font-semibold text-[var(--color-heading)]">Why this person</h3>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          {detail.needReason
            ?? 'Nominated by hand, so there is no recorded gap behind it. That is not a '
              + 'problem, it just means nothing closes when they attend.'}
        </p>
        <p className="mt-2 text-sm text-[var(--color-muted)]">{course.certificateNote}</p>
      </Card>
    </div>
  )
}
